import React, { MutableRefObject } from "react";

import * as NavigationUtils from "../../../lib/util/NavigationUtils";
import * as AnalyticsUtils from "../../../lib/util/AnalyticsUtils";

import { ElementType, ExtractMixinProps } from "../../../lib/types/UtilTypes";
interface BaseErrorBoundaryProps<C extends ElementType> {
	fatal?: boolean;
	tag?: C;
	children?: (error: Error | null) => React.ReactNode | React.ReactFragment;
	resetKey?: string | number;
	errorRef?: MutableRefObject<Error | undefined>;
}

export type ErrorBoundaryProps<C extends ElementType> = ExtractMixinProps<
	BaseErrorBoundaryProps<C>
>;

interface ErrorBoundaryState {
	error: Error | null;
}

export default class ErrorBoundary<
	C extends ElementType = "div",
> extends React.Component<ErrorBoundaryProps<C>, ErrorBoundaryState> {
	state = {
		error: null,
	};

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		const { fatal } = this.props;

		this.setState({ error });
		console.error(`${fatal ? "Fatal error" : "Error"} encountered at boundary`);
		console.error(Object.assign(error, errorInfo));
		AnalyticsUtils.logError(error, Boolean(fatal));
		if (fatal) {
			NavigationUtils.finishTransitions();
		}
	}

	componentDidUpdate(
		prevProps: Readonly<ErrorBoundaryProps<C>>,
		prevState: Readonly<ErrorBoundaryState>,
		snapshot?: any,
	): void {
		let error = this.state.error;
		if (error && prevProps.resetKey !== this.props.resetKey) {
			this.setState({ error: null });
			error = null;
		}
		if (this.props.errorRef) {
			this.props.errorRef.current = error;
		}
	}

	render() {
		const { error } = this.state;
		const { tag, children, fatal, resetKey, errorRef, ...otherProps } =
			this.props;

		return React.createElement(
			tag || "div",
			otherProps,
			typeof children === "function" ? (children as any)(error) : children,
		);
	}
}
